import {
    FC,
    ReactElement,
    useEffect,
    useState,
} from 'react'
import classnames from 'classnames'

import { Button, Icon, ReactionButton } from '@/components'
import { Post } from '@/entities/Post/Post'
import { trans } from '@/helpers/trans'

import './PostActions.scss'

const HEART_BURST_COUNT = 3
const HEART_BURST_DURATION_MS = 1000
const LIKE_ANIMATION_DURATION_MS = 600

const useReactionAnimation = (durationMs: number): [boolean, (shouldPlay: boolean) => void] => {
    const [isPlaying, setIsPlaying] = useState(false)

    useEffect(() => {
        if (! isPlaying) {
            return undefined
        }

        const timeoutId = window.setTimeout(() => setIsPlaying(false), durationMs)

        return () => window.clearTimeout(timeoutId)
    }, [isPlaying, durationMs])

    return [isPlaying, setIsPlaying]
}

interface PostActionsProps {
    post: Pick<Post, 'likeCount' | 'hasLiked' | 'heartCount' | 'hasHearted' | 'commentCount'>
    commentsExpanded: boolean
    onToggleLike: () => void
    onToggleHeart: () => void
    onToggleComments: () => void
}

export const PostActions: FC<PostActionsProps> = ({
    post,
    commentsExpanded,
    onToggleLike,
    onToggleHeart,
    onToggleComments,
}): ReactElement => {
    const [isHeartBursting, playHeartBurst] = useReactionAnimation(HEART_BURST_DURATION_MS)
    const [isLikeAnimating, playLikeAnimation] = useReactionAnimation(LIKE_ANIMATION_DURATION_MS)

    // Only celebrate adding a reaction, not removing it.
    const handleLikeClick = (): void => {
        playLikeAnimation(! post.hasLiked)
        onToggleLike()
    }

    const handleHeartClick = (): void => {
        playHeartBurst(! post.hasHearted)
        onToggleHeart()
    }

    const isLikePressed = post.hasLiked || isLikeAnimating
    const isHeartPressed = post.hasHearted || isHeartBursting

    return (
        <div className="post-actions">
            <ReactionButton
                icon="thumbs-up"
                label={trans('containers.newsfeedContainer.like')}
                count={post.likeCount}
                pressed={isLikePressed}
                className={classnames('post-actions__like', { 'post-actions__like--animating': isLikeAnimating })}
                iconClassName="post-actions__like-icon"
                onClick={handleLikeClick}
            />
            <span className="post-actions__heart-wrapper">
                <ReactionButton
                    icon="heart"
                    label={trans('containers.newsfeedContainer.heart')}
                    count={post.heartCount}
                    pressed={isHeartPressed}
                    className={classnames('post-actions__heart', {
                        'post-actions__heart--pressed': isHeartPressed,
                        'post-actions__heart--bursting': isHeartBursting,
                    })}
                    iconClassName="post-actions__heart-icon"
                    onClick={handleHeartClick}
                />
                {isHeartBursting && (
                    <span className="post-actions__heart-burst" aria-hidden="true">
                        {Array.from({ length: HEART_BURST_COUNT }, (_, index) => (
                            <span key={index} className="post-actions__burst-heart">
                                <Icon name="heart" className="post-actions__burst-heart-icon" />
                            </span>
                        ))}
                    </span>
                )}
            </span>
            <Button
                type="button"
                text={trans('containers.newsfeedContainer.comments')}
                aria-label={trans('containers.newsfeedContainer.comments')}
                aria-pressed={commentsExpanded}
                className={classnames('post-actions__comments', { 'post-actions__comments--pressed': commentsExpanded })}
                onClick={onToggleComments}
            >
                <Icon name="chat-bubble" className="post-actions__comments-icon" />
                {!! post.commentCount && <span className="post-actions__comments-count">{post.commentCount}</span>}
            </Button>
        </div>
    )
}
